import type { Movimentacao, Solicitacao, TipoSolicitacao } from '../../../../models';

/** "AAAA-MM-DD" no fuso local (a data que a pessoa escolheu na abertura, sem deslocar o dia). */
export function chaveDia(data: Date): string {
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
}

function paraDate(valor: Movimentacao['dataPrevista']): Date | null {
  if (!valor) return null;
  return 'toDate' in valor ? valor.toDate() : new Date(valor as unknown as string);
}

export type SituacaoItemAgenda = 'aberta' | 'atrasada' | 'feita';

export interface ItemAgenda {
  solicitacaoId: string;
  movimentacaoId: string;
  dia: string;
  tipoSolicitacao: TipoSolicitacao;
  tipoFluxo: Movimentacao['tipoFluxo'];
  colaboradorNome: string;
  situacao: SituacaoItemAgenda;
}

export interface SemDataAgenda {
  solicitacaoId: string;
  tipoSolicitacao: TipoSolicitacao;
  tipoFluxo: Movimentacao['tipoFluxo'] | null;
  colaboradorNome: string;
  abertaEm: string | null;
}

/**
 * Agenda do Controle de Solicitações: cada Movimentação entra no dia da sua data prevista — numa
 * Troca/Mudança de Projeto, envio e devolução caem cada um no seu dia quando a devolução tem a
 * dela. Sem data própria, vale a data da Solicitação (a da 1ª Movimentação, preenchida na
 * abertura e mostrada no cabeçalho). Cancelada não entra; entregue fica como "feita"; data
 * passada e não entregue, "atrasada". Só vai pra "Sem data prevista" quando nem a Solicitação
 * tem data.
 */
export function itensDaAgenda(
  solicitacoes: Solicitacao[],
  movimentacoesPorSolicitacao: Map<string, Movimentacao[]>,
  hoje: Date
): { itens: ItemAgenda[]; semData: SemDataAgenda[] } {
  const hojeChave = chaveDia(hoje);
  const itens: ItemAgenda[] = [];
  const semData: SemDataAgenda[] = [];
  for (const solicitacao of solicitacoes) {
    const todas = movimentacoesPorSolicitacao.get(solicitacao.id) ?? [];
    // Mesma data do cabeçalho da Solicitação (SolicitacaoDetailsContent: movimentacoes[0]).
    const dataDaSolicitacao = paraDate(todas[0]?.dataPrevista ?? null);
    const movimentacoes = todas.filter((m) => m.statusMovimentacao !== 'cancelada');
    for (const movimentacao of movimentacoes) {
      const data = paraDate(movimentacao.dataPrevista) ?? dataDaSolicitacao;
      const feita = movimentacao.statusMovimentacao === 'entregue';
      if (!data) {
        if (!feita) {
          semData.push({
            solicitacaoId: solicitacao.id,
            tipoSolicitacao: solicitacao.tipoSolicitacao,
            tipoFluxo: movimentacao.tipoFluxo,
            colaboradorNome: solicitacao.colaboradorNome,
            abertaEm: solicitacao.createdAt?.toDate ? chaveDia(solicitacao.createdAt.toDate()) : null,
          });
        }
        continue;
      }
      const dia = chaveDia(data);
      itens.push({
        solicitacaoId: solicitacao.id,
        movimentacaoId: movimentacao.id,
        dia,
        tipoSolicitacao: solicitacao.tipoSolicitacao,
        tipoFluxo: movimentacao.tipoFluxo,
        colaboradorNome: solicitacao.colaboradorNome,
        situacao: feita ? 'feita' : dia < hojeChave ? 'atrasada' : 'aberta',
      });
    }
  }
  return { itens, semData };
}

export interface DiaAgenda {
  chave: string;
  numero: number;
  /** 'atual' = o mês navegado; 'anterior'/'seguinte' = dias de fora, mais apagados. */
  mes: 'anterior' | 'atual' | 'seguinte';
  fimDeSemana: boolean;
}

/**
 * Dias do quadro do mês, de domingo a sábado: começa no domingo da semana do dia 1º e vai até
 * cobrir a primeira semana inteira do mês seguinte (decisão do SI — ver o começo do próximo mês
 * sem trocar de tela).
 */
export function diasDoMes(ano: number, mes: number): DiaAgenda[] {
  const primeiro = new Date(ano, mes, 1);
  const inicio = new Date(ano, mes, 1 - primeiro.getDay());
  const primeiroDoSeguinte = new Date(ano, mes + 1, 1);
  const seteDiasDepois = new Date(ano, mes + 1, 7);
  const fim = new Date(ano, mes + 1, 7 + (6 - seteDiasDepois.getDay()));
  const dias: DiaAgenda[] = [];
  for (let d = new Date(inicio); d <= fim; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    dias.push({
      chave: chaveDia(d),
      numero: d.getDate(),
      mes: d < primeiro ? 'anterior' : d >= primeiroDoSeguinte ? 'seguinte' : 'atual',
      fimDeSemana: d.getDay() === 0 || d.getDay() === 6,
    });
  }
  return dias;
}

/** Os 7 dias (domingo a sábado) da semana que contém `referencia`. */
export function diasDaSemana(referencia: Date): DiaAgenda[] {
  const domingo = new Date(referencia.getFullYear(), referencia.getMonth(), referencia.getDate() - referencia.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(domingo.getFullYear(), domingo.getMonth(), domingo.getDate() + i);
    return { chave: chaveDia(d), numero: d.getDate(), mes: 'atual' as const, fimDeSemana: i === 0 || i === 6 };
  });
}
